import React from "react";
import { api, API } from "@/lib/api";

function sourceLabel(provider) {
  if (provider === "tavily") return "Tavily";
  if (provider === "duckduckgo") return "DuckDuckGo";
  return "Web";
}

export default function ArtifactsPanel({ run, integrationsStatus, onOpenInbox }) {
  if (!run) return null;
  const art = run.artifacts || {};
  const usedMemory = !!art.cross_run_memory_used;
  const sources = art.web_sources || [];
  const provider = art.web_source_provider || (sources[0] && sources[0].source);
  const events = art.calendar_events || [];
  const delivery = art.delivery || null;

  async function downloadIcs() {
    try {
      const r = await api.get(`/runs/${run.id}/calendar.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `neuralcraft-${run.id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("ics download failed:", e?.message || e);
    }
  }

  const showAny = usedMemory || sources.length > 0 || events.length > 0 || delivery;
  if (!showAny && run.status === "running") return null;

  let memoryHeadline = "First run";
  let memoryBody = "Future runs will inherit this one's Memory output.";
  if (usedMemory) {
    memoryHeadline = "Used";
    memoryBody = "Prior Memory snapshots were fed into every agent's context.";
  }

  let sourcesHeadline = sources.length > 0 ? `${sources.length} cited` : "—";
  if (sources.length === 0 && integrationsStatus) sourcesHeadline = "No results";

  let deliveryHeadline = "—";
  let deliveryBody = "Communication agent did not produce content to deliver.";
  if (delivery?.channel === "inbox" && delivery.delivered) {
    deliveryHeadline = "Delivered · inbox";
    deliveryBody = "The Communication agent's status update is waiting in your Inbox.";
  } else if (delivery?.channel === "email" && delivery.sent) {
    deliveryHeadline = "Delivered · email";
    deliveryBody = `Resend id: ${delivery.id}`;
  } else if (delivery?.channel === "email" && !delivery.sent) {
    deliveryHeadline = "Email failed";
    deliveryBody = delivery.reason || "Resend did not accept the message.";
  }

  return (
    <section className="mt-10 lg:mt-14 border-t border-l hairline" data-testid="artifacts-panel">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <div className="md:col-span-3 border-r border-b hairline p-6 lg:p-8 bg-[#071a35]/30">
          <div className="label-xs">III · Artifacts</div>
          <div className="font-serif-display text-3xl italic mt-1">Real outputs</div>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#A8B3C7]">
            Side-effects the Legs produced
          </p>
        </div>

        <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-3">
          {/* Cross-run memory */}
          <div className="border-r border-b hairline p-6" data-testid="memory-card">
            <div className="label-xs">Cross-run memory</div>
            <div className="font-serif-display text-xl mt-1">{memoryHeadline}</div>
            <p className="mt-2 text-[13px] leading-relaxed text-[#A8B3C7]">{memoryBody}</p>
          </div>

          {/* Web sources */}
          <div className="border-r border-b hairline p-6" data-testid="web-sources-card">
            <div className="label-xs">Web sources · {sourceLabel(provider)}</div>
            <div className="font-serif-display text-xl mt-1">{sourcesHeadline}</div>
            {sources.length > 0 ? (
              <ul className="mt-3 space-y-1">
                {sources.slice(0, 4).map((s, i) => (
                  <li key={s.url || i} className="font-mono text-[12px] truncate">
                    <a href={s.url} target="_blank" rel="noreferrer noopener" className="underline underline-offset-2 hover:text-[#00B4E6]">
                      {s.title || s.url}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] leading-relaxed text-[#A8B3C7]">
                Live web search returned no results for this goal.
              </p>
            )}
            {provider === "duckduckgo" && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7A94]">
                free fallback · add TAVILY_API_KEY for higher-quality results
              </p>
            )}
          </div>

          {/* Calendar */}
          <div className="border-r border-b hairline p-6" data-testid="calendar-card">
            <div className="label-xs">Calendar · .ics</div>
            <div className="font-serif-display text-xl mt-1">
              {events.length > 0 ? `${events.length} events` : "—"}
            </div>
            {events.length > 0 ? (
              <>
                <ul className="mt-3 space-y-1 max-h-[120px] overflow-auto">
                  {events.slice(0, 5).map((e, i) => (
                    <li key={i} className="font-mono text-[11px] truncate">
                      <span className="text-[#A8B3C7]">+{e.day_offset ?? i}d ·</span> {e.title}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={downloadIcs}
                  className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] underline underline-offset-4 hover:text-[#00B4E6]"
                  data-testid="download-ics-btn"
                >
                  Download .ics →
                </button>
              </>
            ) : (
              <p className="mt-2 text-[13px] leading-relaxed text-[#A8B3C7]">
                The Automation agent didn't surface dated events for this run.
              </p>
            )}
          </div>

          {/* Delivery (inbox or email) */}
          <div className="border-r border-b hairline p-6 md:col-span-3" data-testid="delivery-card">
            <div className="label-xs">
              Delivery · {delivery?.channel === "email" ? "Resend" : "Inbox"}
            </div>
            <div className="font-serif-display text-xl mt-1">{deliveryHeadline}</div>
            <p className="mt-2 text-[13px] leading-relaxed text-[#A8B3C7]">{deliveryBody}</p>
            {delivery?.channel === "inbox" && delivery.delivered && (
              <button
                onClick={onOpenInbox}
                className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] underline underline-offset-4 hover:text-[#00B4E6]"
                data-testid="open-inbox-btn"
              >
                Open inbox →
              </button>
            )}
            {!delivery && integrationsStatus && !integrationsStatus.resend && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7A94]">
                in-app inbox active · add RESEND_API_KEY for email delivery
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// silence unused import lint
export const __api_url = API;
